// i18n: translated
/**
 * アプリ最上位の React Error Boundary (#255 ポータルホワイトアウト再発防止)
 *
 * render 中の想定外例外をキャッチし、ツリー全体アンマウント (= ホワイトアウト)
 * を防ぐ。fallback UI にはリロードボタンを置き、ユーザーが自力で復帰できるようにする。
 *
 * Admin / Portal 両方をカバーするため main.tsx で `<App />` 外側にラップする。
 * ルーター依存を持たせないために BrowserRouter の外に配置する。
 *
 * 表示言語 (admin-english-toggle phase 2 / ADR-081): root は表示言語の Provider の外にあるので
 * 既定の文言 (日本語) のまま。管理画面の shell (`AppLayout`) の中では `labels` を渡した内側の
 * 境界で画面 (Outlet) を包み、英語表示では英語の文言を出す (ヘッダー・サイドバーは残る)。
 * `resetKey` を渡すと、その値が変わったとき (画面の移動) にエラー表示を解く。
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

/** 既定の fallback の文言。省略時は日本語 (root の境界は表示言語の Provider の外)。 */
export interface ErrorBoundaryLabels {
  message: string;
  reload: string;
}

const DEFAULT_LABELS: ErrorBoundaryLabels = {
  message: '一時的に表示できません。再読み込みしてください。',
  reload: '再読み込み',
};

interface ErrorBoundaryProps {
  children: ReactNode;
  /** カスタム fallback (省略時はデフォルトの「再読み込み」画面) */
  fallback?: ReactNode;
  /** 既定の fallback の文言 (表示言語別に渡す。省略時は日本語) */
  labels?: ErrorBoundaryLabels;
  /**
   * 画面の中に置く境界 (`true`) は、全画面ではなく内容の領域に収まる fallback を出す
   * (ヘッダー・サイドバーを残す)。省略時は従来の全画面表示。
   */
  inline?: boolean;
  /** この値が変わったらエラー表示を解いて子を描き直す (画面の移動で復帰する) */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // render 例外は開発者が把握できるよう console.error に残す (デバッグ目的ではなく障害痕跡)

    console.error('[ErrorBoundary] Unhandled render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    if (this.props.fallback) {
      return this.props.fallback;
    }
    const labels = this.props.labels ?? DEFAULT_LABELS;
    const inline = this.props.inline === true;
    return (
      <div
        role={inline ? 'alert' : undefined}
        className={
          inline
            ? 'flex h-full min-h-[60vh] items-center justify-center px-4'
            : 'flex min-h-screen items-center justify-center bg-gray-50 px-4'
        }
      >
        <div
          className={
            inline
              ? 'w-full max-w-sm rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg dark:border-gray-700 dark:bg-gray-800'
              : 'w-full max-w-sm rounded-xl border border-gray-200 bg-white p-8 text-center shadow-lg'
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="mx-auto mb-4 h-12 w-12 text-amber-500"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
          <p
            className={
              inline
                ? 'mb-4 text-sm text-gray-700 dark:text-gray-300'
                : 'mb-4 text-sm text-gray-700'
            }
          >
            {labels.message}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600"
          >
            {labels.reload}
          </button>
        </div>
      </div>
    );
  }
}
